// In-app browser (KakaoTalk, Instagram, Facebook, NAVER, Line, etc.) detector.
// Google blocks OAuth from these (403 disallowed_useragent), so we prompt the user
// to open the page in their system browser (Chrome / Safari) instead.
import React, { useMemo, useState } from "react";
import { ExternalLink, Copy, ChevronRight, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { isNative } from "../lib/native/platform";

const IN_APP_PATTERNS = [
  { id: "kakaotalk", name: "카카오톡", regex: /KAKAOTALK/i },
  { id: "instagram", name: "인스타그램", regex: /Instagram/i },
  { id: "facebook",  name: "페이스북",   regex: /(FBAN|FBAV|FB_IAB)/i },
  { id: "line",      name: "라인",       regex: /Line\//i },
  { id: "naver",     name: "네이버 앱",   regex: /NAVER\(inapp/i },
  { id: "daum",      name: "다음 앱",     regex: /DaumApps/i },
  { id: "everytime", name: "에브리타임",  regex: /Everytime/i },
  { id: "twitter",   name: "X (트위터)",  regex: /Twitter/i },
  { id: "discord",   name: "디스코드",    regex: /Discord/i },
  { id: "telegram",  name: "텔레그램",    regex: /Telegram/i },
  { id: "tiktok",    name: "TikTok",     regex: /(musical_ly|BytedanceWebview|TikTok)/i },
];

function detectInAppBrowser(ua) {
  for (const p of IN_APP_PATTERNS) {
    if (p.regex.test(ua)) return p;
  }
  // Generic Android WebView ("; wv)")
  if (/Android/i.test(ua) && /; wv\)/i.test(ua)) {
    return { id: "android_webview", name: "앱 내 브라우저 (Android WebView)" };
  }
  return null;
}

function isAndroid(ua) { return /Android/i.test(ua); }
function isIOS(ua) { return /iPhone|iPad|iPod/i.test(ua); }

export default function InAppBrowserGuard() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const detected = useMemo(() => detectInAppBrowser(ua), [ua]);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof sessionStorage !== "undefined") {
      return sessionStorage.getItem("inapp_dismissed") === "1";
    }
    return false;
  });

  // 네이티브(Capacitor APK)는 자체 Android WebView 위에서 동작하지만 네이티브 구글 로그인이
  // 앱 내에서 정상 동작하므로 이 가드가 불필요. 오검지 방지를 위해 비활성(hooks 이후에 판정).
  // 판별 실패/예외 시엔 안전하게 웹 동작(가드 유지)으로 폴백.
  let native = false;
  try { native = isNative(); } catch (e) { native = false; }
  if (native) return null;

  // 공개 페이지(/p/ 상품 공유 착지점)는 로그인이 필요 없으므로 인앱브라우저 안내를 띄우지 않는다.
  // (카톡/SNS로 공유된 링크가 카톡 인앱브라우저에서 열려도 상품이 정상 노출되도록 — 로그인 유도 오버레이 금지.)
  let pathname = "";
  try { pathname = window.location.pathname || ""; } catch (e) { pathname = ""; }
  if (pathname.startsWith("/p/")) return null;

  if (!detected || dismissed) return null;

  const currentUrl = typeof window !== "undefined" ? window.location.href : "";
  const host = typeof window !== "undefined" ? window.location.host + window.location.pathname : "";

  const openInChrome = () => {
    // Android: use intent:// to launch Chrome explicitly with fallback to play store
    if (isAndroid(ua)) {
      const intent = `intent://${host}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(currentUrl)};end`;
      window.location.href = intent;
    } else {
      // iOS doesn't allow opening Safari programmatically — show instructions
      toast.info("iPhone은 Safari로 직접 열어주세요. 아래 'URL 복사' 후 Safari 주소창에 붙여넣기");
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      toast.success("URL이 복사되었습니다. Chrome/Safari에 붙여넣기 하세요.");
    } catch (e) {
      // Fallback: prompt
      window.prompt("아래 URL을 복사한 뒤 Chrome 또는 Safari 주소창에 붙여넣으세요:", currentUrl);
    }
  };

  const dismiss = () => {
    sessionStorage.setItem("inapp_dismissed", "1");
    setDismissed(true);
  };

  return (
    <div
      data-testid="inapp-browser-guard"
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fadeIn"
    >
      <div className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] p-5 text-white relative">
          <button
            data-testid="inapp-dismiss-btn"
            onClick={dismiss}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
            aria-label="닫기"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center mb-3">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <h2 className="text-xl font-black leading-tight">
            {detected.name} 앱에서는<br />Google 로그인이 안 돼요
          </h2>
          <p className="text-white/80 text-sm mt-1.5">
            Google 보안 정책상 인앱 브라우저에서 로그인이 차단됩니다.<br />
            <b>Chrome</b> 또는 <b>Safari</b>에서 다시 열어주세요.
          </p>
        </div>

        <div className="p-5 space-y-2.5">
          {isAndroid(ua) && (
            <button
              data-testid="open-chrome-btn"
              onClick={openInChrome}
              className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-black text-sm py-3.5 rounded-2xl inline-flex items-center justify-center gap-2"
            >
              <ExternalLink className="w-4 h-4" /> Chrome 앱으로 열기
            </button>
          )}

          <button
            data-testid="copy-url-btn"
            onClick={copyUrl}
            className={`w-full font-black text-sm py-3.5 rounded-2xl inline-flex items-center justify-center gap-2 ${
              isAndroid(ua)
                ? "bg-gray-100 hover:bg-gray-200 text-gray-800"
                : "bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
            }`}
          >
            <Copy className="w-4 h-4" /> URL 복사 후 {isIOS(ua) ? "Safari" : "기본 브라우저"}에 붙여넣기
          </button>

          <div className="pt-3 border-t border-gray-100">
            <p className="text-[11px] font-black tracking-wider text-gray-400 uppercase mb-2">또는 수동 안내</p>
            <ol className="text-[12px] text-gray-600 space-y-1.5 leading-relaxed">
              <li className="flex gap-2"><span className="text-[#2563EB] font-bold">1.</span> 화면 <b>오른쪽 위 점 3개(⋮)</b> 또는 <b>···</b> 메뉴 클릭</li>
              {isIOS(ua) ? (
                <li className="flex gap-2"><span className="text-[#2563EB] font-bold">2.</span> <b>&ldquo;Safari로 열기&rdquo;</b> 선택</li>
              ) : (
                <li className="flex gap-2"><span className="text-[#2563EB] font-bold">2.</span> <b>&ldquo;다른 브라우저로 열기&rdquo;</b> 또는 <b>&ldquo;Chrome으로 열기&rdquo;</b> 선택</li>
              )}
              <li className="flex gap-2"><span className="text-[#2563EB] font-bold">3.</span> 열린 페이지에서 Google 로그인 진행 <ChevronRight className="w-3 h-3 inline" /></li>
            </ol>
          </div>

          <div className="pt-2 text-center">
            <button onClick={dismiss} className="text-[11px] text-gray-400 hover:text-gray-600 underline">
              일단 그냥 보기 (로그인은 안 됩니다)
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity:0; transform: translateY(8px); } to { opacity:1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 220ms ease-out; }
      `}</style>
    </div>
  );
}
